import type { IconName } from '@fortawesome/fontawesome-svg-core';
import {
  Avatar,
  Button,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import React, { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { ConfirmationDialog } from '@components/ConfirmationDialog';
import Icon from '@components/Icon/Icon';

import {
  useAuthenticateTerraConnection,
  useDeauthenticateTerraConnection,
} from '@hooks/api/terra';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { getConnectAppName } from '@utils/device-brand';
import { formatDate } from '@utils/format';
import { getImageSrc } from '@utils/image-handler';
import { getScanAppLink, SDK_INTEGRATIONS } from '@utils/scan';

import type {
  TerraIntegration as Integration,
  TerraConnection,
} from 'generated/types';

const messages = defineMessages({
  connect: {
    id: 'devicesMain.connect',
    defaultMessage: 'Connect',
  },
  disconnect: {
    id: 'devicesMain.disconnect',
    defaultMessage: 'Disconnect',
  },
  connected: {
    id: 'devicesMain.connected',
    defaultMessage: 'Connected',
  },
  error: {
    id: 'devicesMain.error',
    defaultMessage:
      'An error occurred while trying to (dis)connect to/from a Terra integration',
  },
  dateConnected: {
    id: 'devicesMain.dateConnected',
    defaultMessage: 'Date Connected: {date}',
  },
  lastUpdate: {
    id: 'devicesMain.lastUpdate',
    defaultMessage: 'Last Update: {date}',
  },
  confirm: {
    id: 'devicesMain.disconnectConfirm',
    defaultMessage: 'Are you sure you want to disconnect this health device?',
  },
  undone: {
    id: 'GLOBAL.cannotUndo',
    defaultMessage: 'This cannot be undone.',
  },
  disclaimer: {
    id: 'devicesMain.disclaimer',
    defaultMessage:
      'If a connected device does not appear as connected or if data from a connected device is not appearing in your profile, {instructions}. Instead, contact support@vitall.com or submit a “Something is missing” report for assistance.',
  },
  instructions: {
    id: 'devicesMain.instructions',
    defaultMessage: 'do not disconnect and reconnect the health device',
  },
});

const SCOPE_ICON_MAP: Record<string, IconName> = {
  activity: 'person-running-fast',
  body: 'person',
  daily: 'chart-simple',
  sleep: 'snooze',
};

interface TerraIntegrationProps {
  /**
   * Name and image of Terra integration
   */
  integration: Integration;
  /**
   * User's connected Terra device
   */
  connection?: TerraConnection;
}

export default function TerraIntegration({
  integration: terraIntegration,
  connection,
}: TerraIntegrationProps) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { activeProfile },
  } = useSession();

  const ref = useRef<HTMLButtonElement | null>(null);

  const [authenticateConnection, authenticateConnectionLoading] =
    useAuthenticateTerraConnection();
  const [deauthenticateConnection, deauthenticateConnectionLoading] =
    useDeauthenticateTerraConnection();

  const loading =
    authenticateConnectionLoading || deauthenticateConnectionLoading;

  const { integration, name, scopes } = terraIntegration;

  // Connect surfaces have to carry the provider's full product name, not the
  // shortened one Terra returns.
  const displayName = getConnectAppName(integration, name);

  const isAuthenticated = connection?.is_authenticated;

  const openScanApp = () => {
    const link = getScanAppLink(activeProfile?.email);
    if (link) {
      open(link, '_blank');
    }
    return;
  };

  const connect = async () => {
    if (SDK_INTEGRATIONS.includes(integration)) {
      return openScanApp();
    }
    const { data } = await authenticateConnection({
      input: {
        vitall_patient_id: activeProfile?.vitall_patient_id ?? '',
        integration,
      },
    });

    if (!data?.authenticateConnection) {
      return;
    }
    const { auth_url } = data.authenticateConnection;

    // Open integration's authentication route
    // Upon authentication completion, the user will be navigated to /device-integrator/[result]
    open(auth_url, '_blank');
  };

  const disconnect = async () => {
    if (!isAuthenticated) {
      return;
    }
    if (SDK_INTEGRATIONS.includes(integration)) {
      return openScanApp();
    }
    const { terra_connection_id } = connection;
    await deauthenticateConnection({
      input: {
        terra_connection_id,
      },
      vitall_patient_id: activeProfile?.vitall_patient_id ?? '',
    });
  };

  const handleClick = async () => {
    try {
      if (isAuthenticated) {
        await disconnect();
      } else {
        await connect();
      }
    } catch (e) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    }
  };
  return (
    <ListItem>
      <ListItemAvatar sx={{ marginRight: '10px' }}>
        {/* Rounded rather than circular: brand app tiles are square artwork
        and cropping one to a circle restyles it. */}
        <Avatar
          sx={{ width: 50, height: 50, borderRadius: '10px' }}
          variant="rounded"
          src={
            getImageSrc(
              `public/terra/brands/${integration.toLowerCase()}.webp`,
              100,
              100
            ) ?? ''
          }
        >
          <Icon name="watch-fitness" size="xl" />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Stack
            alignItems="center"
            direction="row"
            spacing={1}
            flexWrap="wrap"
          >
            <Typography variant="h6">{displayName}</Typography>
            {scopes.map((scope) => (
              <Icon
                color={connection ? 'secondary.main' : 'grey.500'}
                key={`integration-scope-${scope}`}
                name={SCOPE_ICON_MAP[scope]}
              />
            ))}
          </Stack>
        }
        secondary={
          isAuthenticated ? (
            <>
              <Typography variant="subtitle2">
                {formatMessage(messages.dateConnected, {
                  date: formatDate({
                    date: connection.created_at,
                  }),
                })}
              </Typography>
              <Typography variant="subtitle2">
                {formatMessage(messages.lastUpdate, {
                  date: formatDate({
                    date: connection.last_webhook_update,
                    fallback: '-',
                  }),
                })}
              </Typography>
            </>
          ) : null
        }
      />
      {isAuthenticated ? (
        <ConfirmationDialog
          content={
            <Stack spacing={2}>
              <Typography color="text.secondary" variant="body2">
                {formatMessage(messages.undone)}
              </Typography>

              <Typography color="text.secondary" variant="body2">
                {formatMessage(messages.disclaimer, {
                  instructions: <b>{formatMessage(messages.instructions)}</b>,
                })}
              </Typography>
            </Stack>
          }
          onConfirm={handleClick}
          title={formatMessage(messages.confirm)}
        >
          {({ handleOpen }) => (
            <Button
              disabled={loading}
              onClick={handleOpen}
              ref={ref}
              onMouseOut={() => {
                if (ref.current?.innerText) {
                  ref.current.innerText = formatMessage(messages.connected);
                }
              }}
              onMouseOver={() => {
                if (ref.current?.innerText) {
                  ref.current.innerText = formatMessage(messages.disconnect);
                }
              }}
              sx={{
                backgroundColor: 'success.main',
                '&:hover': {
                  backgroundColor: 'error.main',
                },
              }}
            >
              {formatMessage(messages.connected)}
            </Button>
          )}
        </ConfirmationDialog>
      ) : (
        <Button
          disabled={loading}
          onClick={handleClick}
          sx={{
            backgroundColor: 'primary.main',
          }}
        >
          {formatMessage(messages.connect)}
        </Button>
      )}
    </ListItem>
  );
}
